import React from 'react';
import { istDay } from '../../utils/dates';

// Reports load a recent window by default instead of every lead ever created,
// so they stay fast as a workspace grows. Picking explicit dates overrides it;
// "All time" opts back into the full history.
export const DEFAULT_RANGE = '30d';

export const RANGE_OPTIONS = [
  { value: '7d',     label: 'Last 7 days' },
  { value: '30d',    label: 'Last 30 days' },
  { value: '90d',    label: 'Last 90 days' },
  { value: '180d',   label: 'Last 6 months' },
  { value: '365d',   label: 'Last 1 year' },
  { value: 'all',    label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// India days, as the server counts them (utils/dates)
const ymd = (d) => istDay(d);

/** The last 30 days, used to prefill a custom range so it is valid the moment
 *  it is picked rather than silently falling back to the default window. */
export function defaultCustomDates() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { startDate: ymd(start), endDate: ymd(end) };
}

export const BLANK_RANGE_FILTERS = { range: DEFAULT_RANGE, startDate: '', endDate: '' };

export function RangeSelect({ value, onChange, disabled, className = '' }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      title={disabled ? 'Clear the dates to use a preset range' : 'How far back to load'}
      className={`input text-sm w-auto ${disabled ? 'bg-gray-50 text-gray-400' : ''} ${className}`}
    >
      {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

/**
 * The window control the Dashboard and every report share: a preset dropdown
 * that reveals From/To inputs when "Custom range" is picked.
 *
 * The API decides the window from whichever of range / startDate / endDate it
 * receives, and explicit dates win — so a preset must clear the dates, and
 * custom must carry them. Keeping that in one place is why this is a
 * component rather than three copies of a select and two date inputs.
 *
 * `value` is { range, startDate, endDate }; `onChange` gets the whole object.
 */
export function RangePicker({ value, onChange, className = '', inputClassName = 'input text-sm w-auto', labelClassName = 'text-xs text-gray-500' }) {
  const isCustom = value.range === 'custom';

  const pickRange = (range) => {
    if (range === 'custom') {
      // Prefill, so picking "Custom range" shows a real window immediately.
      onChange({ range, ...(value.startDate || value.endDate
        ? { startDate: value.startDate, endDate: value.endDate }
        : defaultCustomDates()) });
    } else {
      onChange({ range, startDate: '', endDate: '' });
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <select
        value={value.range}
        onChange={e => pickRange(e.target.value)}
        title="How far back to load"
        className={inputClassName}
      >
        {RANGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      {isCustom && (
        <>
          <span className={labelClassName}>From</span>
          <input
            type="date"
            value={value.startDate || ''}
            max={value.endDate || undefined}
            onChange={e => onChange({ ...value, range: 'custom', startDate: e.target.value })}
            className={inputClassName}
          />
          <span className={labelClassName}>To</span>
          <input
            type="date"
            value={value.endDate || ''}
            min={value.startDate || undefined}
            onChange={e => onChange({ ...value, range: 'custom', endDate: e.target.value })}
            className={inputClassName}
          />
        </>
      )}
    </div>
  );
}

/** What to send the API for a given picker value — never both a preset and dates. */
export function rangeParams(value) {
  if (value.range === 'custom') {
    return { startDate: value.startDate || undefined, endDate: value.endDate || undefined };
  }
  return { range: value.range };
}

const fmt = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

/** Small line under the filters saying which window the numbers cover. */
export function RangeNote({ applied }) {
  if (!applied) return null;
  if (applied.range === 'all') {
    return <p className="text-xs text-gray-400 mt-2">Showing every lead ever created.</p>;
  }
  if (applied.range === 'custom') {
    return (
      <p className="text-xs text-gray-400 mt-2">
        Showing leads created {applied.startDate ? `from ${fmt(applied.startDate)}` : ''}
        {applied.endDate ? ` to ${fmt(applied.endDate)}` : ''}.
      </p>
    );
  }
  return (
    <p className="text-xs text-gray-400 mt-2">
      Showing leads created since {applied.startDate ? fmt(applied.startDate) : '—'} — pick
      <span className="font-medium text-gray-500"> All time </span>
      above to include older leads.
    </p>
  );
}

/** The window the API actually applied, as YYYY-MM-DD for drill-down links. */
export function drillDates(applied) {
  const ymd = (d) => (d ? istDay(d) : '');
  return { startDate: ymd(applied?.startDate), endDate: ymd(applied?.endDate) };
}
